// Shared formatting helpers for the unified Create surface.
//
// The contract score scale is int 1-100 (oracleApi Verdict.overall_score,
// vaultApi VaultIndexEntry.overall_score); the design renders everything on
// a /10 scale with one decimal (e.g. 72 -> "7.2"). These helpers are the
// single conversion point.

// ── WHY null IS CHECKED SEPARATELY ──────────────────────────────────────────
//
// The `Number.isFinite` guard below looks like it covers a missing score. It
// does not, because JavaScript coerces the two most likely representations of
// "missing" to a real number:
//
//   Number(null)       -> 0        Number('')    -> 0
//   Number(undefined)  -> NaN      Number('x')   -> NaN
//
// So a withheld score arriving as null rendered "0.0" — a real-looking reading
// of zero out of ten — instead of the em dash. listVaultEntries returns exactly
// that for a sealed row, so every locked scan on Home, the Vault and the reveal
// modal read as a catastrophic score rather than a hidden one.
//
// Guard the absent cases explicitly. Only genuine numbers reach the coercion.
export function formatScore10(overallScore) {
  if (overallScore === null || overallScore === undefined || overallScore === '') return '—';
  const n = Number(overallScore);
  if (!Number.isFinite(n)) return '—';
  return (n / 10).toFixed(1);
}

// Learned-model calibrated percentile (0..1) -> ordinal display
// ("0.73" -> "73rd"). Clamped to the 1st–99th band so the edges never read as
// an absolute "0th"/"100th". Returns null for a missing/invalid percentile so
// callers can hide the metric entirely.
export function formatPercentile(p) {
  if (typeof p !== 'number' || !Number.isFinite(p)) return null;
  const pct = Math.min(99, Math.max(1, Math.round(p * 100)));
  const mod100 = pct % 100;
  const mod10 = pct % 10;
  let suffix = 'th';
  if (mod100 < 11 || mod100 > 13) {
    if (mod10 === 1) suffix = 'st';
    else if (mod10 === 2) suffix = 'nd';
    else if (mod10 === 3) suffix = 'rd';
  }
  return `${pct}${suffix}`;
}

// taken_at is user-specified and may be null (vaultApi contract); fall back
// to the submission timestamp so every entry still gets a date + ordering.
export function scanDateMs(entry) {
  if (!entry) return null;
  const iso = entry.taken_at || entry.timestamp;
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

export function formatScanDate(entry) {
  const ms = scanDateMs(entry);
  if (ms == null) return '—';
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
